/**
 * Reverse Transformation Adapters
 *
 * Transforms display objects back to client DTOs for updates
 */

import { EventApi, EventChangeArg } from "@fullcalendar/core";
import { CalendarEvent, CalendarEventExtendedProps, ClientLunchBreak, ClientWorkingHours, ClientTimeSlot, TimeSlotUpdateData, ClientContinuousLunchBreak, isGroupPatientType, isIntervalPriced, ZEITINTERVALL_MINUTES } from "../types";
import { AppointmentDto, TimeSlotDto, ContinuousAppointmentDto } from "../../../../../api-client";
import moment from "moment";

/** `targetGroupAppointmentId` is set by the calendar when the drop lands exactly on an existing KGG group. */
export type EventChangeArgWithGroupTarget = EventChangeArg & { targetGroupAppointmentId?: string };

/**
 * Transform FullCalendar Event back to update data
 */
export const adaptCalendarEventToUpdateData = (event: CalendarEvent): TimeSlotUpdateData => {
  return {
    id: event.timeSlotId || event.id,
    start: new Date(event.start),
    end: new Date(event.end),
    appointmentId: event.appointmentId,
    userId: event.resourceId,
  };
};

/**
 * Trim an interval-priced slot to a whole number of Zeitintervalle.
 *
 * § 125a Ergotherapie bills only COMPLETED intervals (Anlage 1 C(2):
 * "die Abrechnung der abgeschlossenen Zeitintervalle à 15 Minuten"), and the
 * calendar snaps to 5 minutes. Without this, a therapist dragging a treatment
 * to 40 minutes would be paid for 30 and have nothing on screen to say so —
 * the slot would sit there claiming ten minutes that are not billable.
 *
 * Trimming at the point of SAVE keeps what is stored, what is shown and what
 * is billed the same number. Slots that are not interval-priced are returned
 * untouched: their duration is a Regelbehandlungszeit and is not the
 * therapist's to set.
 */
const snapToWholeIntervals = (
  start: Date | null,
  end: Date | null,
  therapy?: { pricingBasis?: string } | null,
): Date | null => {
  if (!start || !end || !isIntervalPriced(therapy)) return end;
  const minutes = (end.getTime() - start.getTime()) / 60000;
  const completed = Math.floor(minutes / ZEITINTERVALL_MINUTES);
  // Below one whole interval there is nothing to trim to; leave it for the
  // backend and the validators to reject rather than silently resizing to zero.
  if (completed < 1) return end;
  return new Date(start.getTime() + completed * ZEITINTERVALL_MINUTES * 60000);
};

export const eventToTimeSlotDto = (event: EventApi): TimeSlotDto => {
  const props: CalendarEventExtendedProps = event.extendedProps as CalendarEventExtendedProps;
  const snappedEnd = snapToWholeIntervals(event.start, event.end, props.therapy);

  return {
    id: event.id,
    start: event.start ? event.start.toISOString() : "",
    end: snappedEnd ? snappedEnd.toISOString() : "",
    appointmentId: props.appointmentId,
    type: props.timeSlotType ,
    user: { id: props.user?.id},
    // The line is sent as an IDENTITY only — its id plus the Rx it belongs to.
    // `TherapyRx` carries no performed therapy (only the prescribed term), and
    // under MD-232 a client-supplied line item is not an authority anyway: the
    // backend re-resolves from the term and keeps the slot's persisted item as
    // the fallback.
    //
    // NB: no `as any` here on purpose. It used to carry a `therapy` key that
    // matched no field on TherapyRxDto — the cast is what let it through, and a
    // reader who saw it reasonably concluded the payload shipped a line item.
    therapyRx: props.therapy && props.rx ? {
      id: props.therapyRxId,
      rx: {
        id: props.rx.id,
        rxNumber: props.rx.rxNumber,
      },
    } : undefined,
  };
};

export const eventToClientTimeSlot = (event: CalendarEvent): ClientTimeSlot => {
  // console.debug("Transforming CalendarEvent to ClientTimeSlot:", event);
  const props: CalendarEventExtendedProps = event.extendedProps as CalendarEventExtendedProps;
  return {
    id: event.id,
    start: event.start ? new Date(event.start) : undefined as any,
    end: event.end ? new Date(event.end) : undefined as any,
    type: props.timeSlotType,
    user: props.user,
    patient: props.patient,
    therapy: props.therapy,
    containsHeatTreatment: props.containsHeatTreatment,
    appointmentId: props.appointmentId,
    treatmentIndex: props.treatmentIndex,
    wasDocumentedCount: props.wasDocumentedCount,
    rx: props.rx ? props.rx : undefined,
    // treatmentIndex: props.treatmentIndex,
    attended: props.attended,
    informing: props.informing,

    title: event.title, // not from props?
    loaType: props.loaType,
    isCancelled: props.isCancelled,
    reason: props.reason,
    hasNotes: props.hasNotes,

    visitFee: props.visitFee,
    visitType: props.visitType,

    sublocality: props.sublocality,
    isContinuous: props.isContinuous ,
    groupAppointmentId: props.groupAppointmentId,
    participants: props.participants || [],

    // userId: props.user?.id,
  };
}



export const eventToCalendarEvent = (event: EventApi): CalendarEvent => {
 return event as any as CalendarEvent;
};

/**
 * Materialize a CalendarEvent that may really be a LIVE FullCalendar EventApi
 * (eventToCalendarEvent is just a cast) into a plain object. EventApi exposes
 * id/groupId/start/end as prototype GETTERS, so an object spread
 * ({ ...event, extendedProps: ... }) silently drops all of them — the
 * "Nur diese" move flow lost its slot id exactly this way. Any flow that
 * spreads an event to attach flags (e.g. moveSingleSlot) must materialize
 * first.
 */
export const materializeCalendarEvent = (event: CalendarEvent): CalendarEvent =>
  ({
    id: event.id,
    groupId: event.groupId,
    title: (event as any).title,
    start: event.start,
    end: event.end,
    extendedProps: { ...event.extendedProps },
  }) as CalendarEvent;


export const eventChangeToTimeSlotDto = (eventChangeInfo: EventChangeArg): TimeSlotDto => {
  const props: CalendarEventExtendedProps = eventChangeInfo.event.extendedProps as CalendarEventExtendedProps;
  const userId = eventChangeInfo.event.getResources()?.[0]?.id ;

  return {
    id: eventChangeInfo.event.id,
    start: eventChangeInfo.event.start ? eventChangeInfo.event.start.toISOString() : "",
    end: eventChangeInfo.event.end ? eventChangeInfo.event.end.toISOString() : "",
    appointmentId: props.appointmentId, // normally there should be no appointmentId, because this is used for non-app events
    type: props.timeSlotType ,
    user: { id: userId }
  };
};

export const eventChangeToAppointmentUpdateData = (
  eventChangeInfo: EventChangeArgWithGroupTarget): AppointmentDto => {
  const { event } = eventChangeInfo;
  const userId = eventChangeInfo.event.getResources()?.[0]?.id ;
  if (!userId) {
    throw new Error("Event must have a resourceId (userId) to map to TimeSlot");
  }
  const timeSlots = [eventToTimeSlotDto(eventChangeInfo.event)];
  for (const ev of eventChangeInfo.relatedEvents) {
    timeSlots.push(eventToTimeSlotDto(ev));
  }

  // assign new user to all timeslots:
  timeSlots.forEach(ts => ts.user = { id: userId });

  // Without the group's id the backend creates a second group next to the existing one.
  if (eventChangeInfo.targetGroupAppointmentId) {
    timeSlots.forEach(ts => {
      if (isGroupPatientType(ts.type)) ts.groupAppointmentId = eventChangeInfo.targetGroupAppointmentId;
    });
  }

  const existingFrequency = (event.extendedProps as any)?.frequency;
  const appDto: AppointmentDto = {
    id: event.extendedProps.appointmentId,
    start: event.start ? event.start.toISOString() : undefined,
    end: event.end ? event.end.toISOString() : undefined,
    timeSlots: timeSlots,
    frequency:
      existingFrequency && (existingFrequency.id || existingFrequency.text || existingFrequency.prefferedValue != null)
        ? {
            id: existingFrequency.id,
            text: existingFrequency.text,
            prefferedValue: existingFrequency.prefferedValue,
          }
        : (undefined as any),
  };
  return appDto;
};

/**
 * Transform a FullCalendar EventChangeArg into a ContinuousAppointmentDto update payload
 * Includes the changed event, related grouped events and any externally supplied related events
 * Ensures user reassignment across all involved timeslots.
 */
export const eventChangeToContinuousAppointmentUpdateData = (
  eventChangeInfo: EventChangeArg
): ContinuousAppointmentDto => {
  const { event } = eventChangeInfo;
  const userId = eventChangeInfo.event.getResources()?.[0]?.id;
  if (!userId) {
    throw new Error("Event must have a resourceId (userId) to map to Continuous TimeSlot");
  }

  // Base timeslots: changed event + grouped related events from FullCalendar
  const timeSlots: TimeSlotDto[] = [eventToTimeSlotDto(eventChangeInfo.event)];
  for (const ev of eventChangeInfo.relatedEvents) {
    timeSlots.push(eventToTimeSlotDto(ev));
  }

  // Reassign user across all involved timeslots (user change or drag to new resource)
  timeSlots.forEach(ts => {
    ts.user = { id: userId } as any; // keep lightweight user reference
  });

  // Compute aggregate start/end (earliest start, latest end) for the continuous appointment
  const sortedByStart = [...timeSlots].sort((a, b) => new Date(a.start!).getTime() - new Date(b.start!).getTime());
  const sortedByEnd = [...timeSlots].sort((a, b) => new Date(a.end!).getTime() - new Date(b.end!).getTime());
  const aggregateStart = sortedByStart[0]?.start;
  const aggregateEnd = sortedByEnd[sortedByEnd.length - 1]?.end;
  
  const continuousDto: ContinuousAppointmentDto = {
    id: event.extendedProps.appointmentId,
    start: aggregateStart,
    end: aggregateEnd,
    timeSlots: timeSlots,
    frequency: (event.extendedProps as any)?.frequency
      ? {
          id: (event.extendedProps as any).frequency.id,
          text: (event.extendedProps as any).frequency.text,
          prefferedValue: (event.extendedProps as any).frequency.prefferedValue,
        }
      : {
          id: 'UNKNOWN',
          text: 'UNSPECIFIED',
          prefferedValue: timeSlots.length,
        },
  } as ContinuousAppointmentDto;

  return continuousDto;
};


// TODO we need a changeEvent version of this ....
export const eventToClientLunchBreak = (event: EventApi): ClientLunchBreak => {
  const props: CalendarEventExtendedProps = event.extendedProps as CalendarEventExtendedProps;
 
  return {
    id: event.id,
    slotId: props.slotId, // Original API slotId (may be undefined)
    start: event.start ? new Date(event.start) : undefined as any,
    end: event.end ? new Date(event.end) : undefined as any,
    userId: props.user?.id! ,
    isException: props.isException || false,
    exceptionId: props.exceptionId,
  };
}

export const eventToClientContinuousLunchBreak = (event: EventApi): ClientContinuousLunchBreak => {
  const props: CalendarEventExtendedProps = event.extendedProps as CalendarEventExtendedProps;
 
  return {
    id: event.id,
    slotId: props.slotId, // Original API slotId (may be undefined)
    start: event.start ? new Date(event.start) : undefined as any,
    end: event.end ? new Date(event.end) : undefined as any,
    startTime: moment(event.start).format("HH:mm:ss"),
    endTime: moment(event.end).format("HH:mm:ss"),
    userId: props.user?.id! ,
   weeklyWorkDay: props?.weeklyWorkDay!,
  };
}

export const eventToClientWorkingHours = (event: EventApi): ClientWorkingHours => {
  const props: CalendarEventExtendedProps = event.extendedProps as CalendarEventExtendedProps;
 
  return {
    id: event.id,
    slotId: props.slotId, // Original API slotId (may be undefined)
    start: event.start ? new Date(event.start) : undefined as any,
    end: event.end ? new Date(event.end) : undefined as any,
    userId: props.user?.id! ,
    isException: props.isException || false,
    exceptionId: props.exceptionId,
  };
}


